import React, { Fragment, useEffect, useRef, useState } from "react";
import commonStyles from "@styles/CommonPageLayout.module.css";
import { useSearchParams } from "react-router-dom";
import { ServiceSheet } from "@models/ServiceSheet";
import ServiceSheetPDFDownloadButton from "@components/misc/ServiceSheetPDF/ServiceSheetPDFDownloadButton";
import ServiceSheetPDFViewer from "@components/misc/ServiceSheetPDF/ServiceSheetPDFViewer";
import QRCode, { QRCodeHandle } from "@components/display/QRCode/QRCode";
import { getRootDomain } from "@util/domain";
import LoadSaveErrorDisplay from "@components/display/LoadSaveErrorDisplay/LoadSaveErrorDisplay";
import { Memorial } from "@models/Memorial";
import { fetchMemorial } from "@services/memorialService";
import { Business } from "@models/Business";
import { Charity } from "@models/Charity";
import { fetchBusiness } from "@services/businessService";
import { fetchCharity } from "@services/charityService";
import { PageSize } from "@components/misc/ServiceSheetPDF/ServiceSheetDocument";
import Button from "@components/buttons/Button/Button";

function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            resolve(reader.result as string);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

const ServiceSheetPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const id = searchParams.get("id");
    const exampleBusinessId = searchParams.get("exampleBusinessId"); // For example only

    const [memorial, setMemorial] = useState<Memorial>();
    const [business, setBusiness] = useState<Business>();
    const [charity, setCharity] = useState<Charity>();
    const [pageSize, setPageSize] = useState<PageSize>("A5");
    const [loading, setLoading] = useState(true);
    const [qrCodeBase64, setQrCodeBase64] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const qrRef = useRef<QRCodeHandle>(null);

    // Fetch services on component mount
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                if (id) {
                    const data = await fetchMemorial(id);
                    setMemorial(data);
                    if (data.businessId) {
                        const fetchedBusiness = await fetchBusiness(data.businessId);
                        setBusiness(fetchedBusiness);
                    }
                    if (data.charityId) {
                        const fetchedCharity = await fetchCharity(data.charityId);
                        setCharity(fetchedCharity);
                    }
                } else if (exampleBusinessId) {
                    const fetchedBusiness = await fetchBusiness(exampleBusinessId);
                    setBusiness(fetchedBusiness);

                    const EXAMPLE_MEMORIAL: Memorial = {
                        id: "1",
                        firstName: "Aldo",
                        lastName: "Crapis",
                        birthDate: new Date("1966-11-09"),
                        deathDate: new Date("2024-02-24"),
                        image: "/assets/demo/image.jpg",
                        serviceDateTime: new Date("2024-03-11T11:00:00.000+00:00"),
                        serviceLocation: "Our Lady of Good Counsel Church",
                        obituary: "",
                        timeline: [],
                        gallery: [],
                        tributes: [],
                        guestBook: [],
                        pageAdminEmail: "",
                        setAdminStatus: "not set",
                        businessId: exampleBusinessId,
                        previewKey: "",
                        qrKey: "",
                        status: "active",
                        privacy: {
                            published: true,
                            noIndex: false,
                            guestBookOpen: false,
                            tributesOpen: false,
                            emailOnTribute: false,
                            restrictPostContentToQr: false,
                            restrictViewInfoToQr: false,
                            restrictViewTimelineGalleryToQr: false,
                            restrictViewTributesToQr: false,
                        },
                    };
                    setMemorial(EXAMPLE_MEMORIAL);
                } else {
                    throw new Error("No ID.");
                }
            } catch (err) {
                setError("Failed to fetch service sheet.");
            } finally {
                setLoading(false);
            }
        };

        void fetchData();
    }, [exampleBusinessId, id]);

    useEffect(() => {
        const processQr = async () => {
            if (!loading) {
                try {
                    // Wait for a short delay to ensure the QR code has rendered
                    await new Promise((resolve) => { setTimeout(resolve, 100) });
                    const image = await qrRef.current?.captureImage();
                    if (!image) {
                        throw new Error("No image.");
                    }
                    const base64 = await fileToBase64(image);
                    setQrCodeBase64(base64);
                } catch {
                    setError("Failed to generate QR code.");
                }
            }
        };
        void processQr();
    }, [loading]);

    const renderContent = () => {
        if (loading || error) {
            return <LoadSaveErrorDisplay isLoading={loading} error={error} />;
        }

        if (!memorial) {
            return <div className={`${commonStyles.noContent} ${commonStyles.note}`}>No memorial</div>;
        }

        const serviceSheet: ServiceSheet = {
            memorialId: memorial.id || "",
            firstName: memorial.firstName,
            lastName: memorial.lastName,
            birthDate: memorial.birthDate,
            deathDate: memorial.deathDate,
            image: memorial.image || "/assets/images/placeholder-person.png",
            serviceDateTime: memorial.serviceDateTime,
            serviceLocation: memorial.serviceLocation,
            businessLogo: business?.logo || "/assets/images/fioh-text-logo.png",
            charityLogo: exampleBusinessId ? "/assets/demo/heart-foundation.jpg" : charity?.logo || "",
            qrKey: memorial.qrKey,
        };

        return (
            <>
                <div className={commonStyles.headerContainer}>
                    <h1>{`${memorial?.firstName} ${memorial?.lastName} - Service Sheet`}</h1>
                    <div className={commonStyles.buttonGroup}>
                        <Button
                            variant="secondary"
                            size="medium"
                            onClick={() => setPageSize((prev) => (prev === "A4" ? "A5" : "A4"))}
                        >
                            {`Change to ${pageSize === "A4" ? "A5" : "A4"}`}
                        </Button>
                        <ServiceSheetPDFDownloadButton
                            serviceSheet={serviceSheet}
                            qrCodeBase64={qrCodeBase64 || ""}
                            pageSize={pageSize}
                            overrideName={exampleBusinessId ? `${business?.name}_ServiceSheet` : undefined}
                        />
                    </div>
                </div>
                <ServiceSheetPDFViewer
                    serviceSheet={serviceSheet}
                    qrCodeBase64={qrCodeBase64 || ""}
                    pageSize={pageSize}
                />
            </>
        );
    };

    let qrUrl = "";
    if (exampleBusinessId) {
        qrUrl = `${getRootDomain(business?.subdomain || "memorials")}/memorials/example`;
    } else {
        qrUrl = `http://fioh.au/${id}/${memorial?.qrKey}`;
    }

    return (
        <div className={commonStyles.container}>
            {renderContent()}
            <div style={{ display: "none" }}>
                <QRCode
                    ref={qrRef}
                    value={qrUrl}
                    size={10}
                />
            </div>
        </div>
    );
};

export default ServiceSheetPage;
